import React from 'react';
import styles from './ManageTickets.module.css';

export type EventStatusBadgeSource = string | null | undefined | Record<string, any>;

export type EventStatusTone = 'open' | 'closed' | 'pending' | 'tbd' | 'unknown';

function normalizeStatusToken(rawStatus?: string | null): string {
  return String(rawStatus || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export function humanizeEventStatus(raw: string): string {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';

  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Color/tone bucket used for badge styling. */
export function getEventStatusKey(rawStatus?: string | null): EventStatusTone | '' {
  const key = normalizeStatusToken(rawStatus);
  if (!key) return '';

  if (
    key === 'closed' ||
    key === 'cancelled' ||
    key === 'canceled' ||
    key === 'unnecessary' ||
    key === 'deactivated' ||
    key === 'offsale'
  ) {
    return 'closed';
  }

  if (key === 'onsale' || key === 'active' || key === 'scheduled') {
    return 'open';
  }

  if (key === 'postponed' || key === 'rescheduled') {
    return 'pending';
  }

  if (key === 'tbd' || key === 'datetbd' || key === 'timetbd') {
    return 'tbd';
  }

  return 'unknown';
}

/** True when TM/event status is Date TBD (or generic TBD) — no concrete game day. */
export function isDateTbdEventStatus(rawStatus?: string | null): boolean {
  const key = normalizeStatusToken(rawStatus);
  // date-tbd / Date TBD / tbd — not time-tbd (those still have a calendar day).
  return key === 'datetbd' || key === 'tbd' || (key.includes('datetbd') && !key.includes('timetbd'));
}

/** Resolve TM event status from flat or nested ticket/event shapes. */
export function resolveEventStatus(source?: EventStatusBadgeSource): string {
  return resolveRawStatus(source);
}

/**
 * True when the event has no usable calendar day (null/empty/unparseable),
 * or TM status is Date TBD — same cases the UI shows as N/A / Date TBD.
 */
export function isEventDateTbdForVerification(source?: EventStatusBadgeSource): boolean {
  if (!source || typeof source === 'string') {
    return isDateTbdEventStatus(source);
  }

  const status = resolveRawStatus(source);
  if (isDateTbdEventStatus(status)) return true;

  const eDate =
    source.e_date ??
    source.eventDateRaw ??
    source.event?.e_date ??
    source.ticket?.event?.e_date ??
    null;
  const eDateTimeZone =
    source.e_date_time_zone ??
    source.event?.e_date_time_zone ??
    source.ticket?.event?.e_date_time_zone ??
    null;

  const dateStr = String(eDate || '').trim();
  const tzStr = String(eDateTimeZone || '').trim();
  if (!dateStr && !tzStr) return true;
  if (!dateStr) return true;
  // Unparseable / placeholder dates are treated like TBD for verification gates.
  if (/^(tbd|n\/a|na|null|undefined|-|–|—)$/i.test(dateStr)) return true;
  if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr) && Number.isNaN(Date.parse(dateStr))) return true;

  return false;
}

/** Display label for a raw e_status value. */
export function getEventStatusLabel(rawStatus?: string | null): string {
  const raw = String(rawStatus || '').trim();
  if (!raw) return '';

  const key = normalizeStatusToken(raw);

  const labels: Record<string, string> = {
    closed: 'Closed',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    unnecessary: 'Unnecessary',
    deactivated: 'Deactivated',
    offsale: 'Off Sale',
    onsale: 'On Sale',
    active: 'Active',
    scheduled: 'Scheduled',
    postponed: 'Postponed',
    rescheduled: 'Rescheduled',
    tbd: 'TBD',
    datetbd: 'Date TBD',
    timetbd: 'Time TBD',
    hidden: 'Hidden',
    removal: 'Hidden',
  };

  return labels[key] || humanizeEventStatus(raw);
}

function resolveRawStatus(source?: EventStatusBadgeSource): string {
  if (typeof source === 'string') {
    return String(source || '').trim();
  }

  if (source && typeof source === 'object') {
    return String(
      source.e_status ||
      source.eventStatus ||
      source.rawStatus ||
      source.event?.e_status ||
      source.event?.status ||
      '',
    ).trim();
  }

  return '';
}

/** Event status text badge for Manage Tickets, User Details, and Event Update. */
export function renderEventStatusBadge(source?: EventStatusBadgeSource): React.ReactNode {
  const raw = resolveRawStatus(source);
  if (!raw) return null;

  const tone = getEventStatusKey(raw);
  const label = getEventStatusLabel(raw);

  let colorClass = styles.eventStatusDefault;
  if (tone === 'closed') colorClass = styles.eventStatusClosed;
  else if (tone === 'open') colorClass = styles.eventStatusOnSale;
  else if (tone === 'pending') colorClass = styles.eventStatusPending;
  else if (tone === 'tbd') colorClass = styles.eventStatusTbd;

  return (
    <span
      className={`${styles.eventStatusBadge} ${colorClass}`}
      title={`Event ${label}`}
      aria-label={`Event ${label}`}
    >
      {label.toLowerCase()}
    </span>
  );
}
